/**
 * <CategoryCard/> + <CategoryChip/> + <SubCategoryChips/> — r7.6.4
 *
 *   Paint by the category's own colour (from the taxonomy, admin-editable) — never by the 5 theme accents.
 *   Art: the admin-uploaded flower illustration when present, else a large flower glyph tinted in the colour.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/themes/ThemeContext';
import { catColour, FLOWER_GLYPH } from '@/lib/taxonomy';

function useMode() {
  try { return useTheme().activeMode || 'dark'; } catch { return 'dark'; }
}

export function CategoryArt({ cat, size = 96, className = '' }) {
  const mode = useMode();
  const colour = catColour(cat, mode);
  if (cat?.art_url) {
    return <img src={cat.art_url} alt="" className={`object-contain ${className}`} style={{ width: size, height: size }} loading="lazy" />;
  }
  return (
    <span aria-hidden="true" className={`font-serif leading-none select-none ${className}`} style={{ fontSize: size * 0.82, color: colour, opacity: 0.85 }}>
      {FLOWER_GLYPH[cat?.slug] || '✿'}
    </span>
  );
}

export function CategoryCard({ cat, compact = false, onSelect }) {
  const mode = useMode();
  const colour = catColour(cat, mode);
  return (
    <Link to={`/c/${cat.slug}`} onClick={onSelect} data-testid="category-card" data-slug={cat.slug}
      className={`no-underline rounded-card relative overflow-hidden flex flex-col justify-end transition-transform hover:scale-[1.01] ${compact ? 'p-4 aspect-[4/3]' : 'p-5 aspect-[16/10]'}`}
      style={{ background: `linear-gradient(160deg, color-mix(in srgb, ${colour} 28%, var(--snt-bg-2)) 0%, var(--snt-bg-2) 70%)`, border: `1px solid color-mix(in srgb, ${colour} 35%, var(--snt-line))` }}>
      <div className="absolute" style={{ right: 14, top: 8 }}>
        <CategoryArt cat={cat} size={compact ? 64 : 96} />
      </div>
      {cat.flower && !compact && (
        <span className="snt-micro z-10 mb-1" style={{ color: colour }}>{cat.flower}{cat.tagline ? ` · ${cat.tagline}` : ''}</span>
      )}
      <span className="font-serif z-10" style={{ fontSize: compact ? 18 : 24, letterSpacing: '-0.02em', color: 'var(--snt-text)', lineHeight: 1.1 }}>{cat.name}</span>
      {typeof cat.event_count === 'number' && cat.event_count > 0 && (
        <span className="text-[11px] z-10 mt-1" style={{ color: 'var(--snt-text-3)' }}>{cat.event_count} upcoming</span>
      )}
    </Link>
  );
}

export function CategoryChip({ cat, active = false, to, onClick }) {
  const mode = useMode();
  const colour = catColour(cat, mode);
  const cls = 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[12px] font-medium no-underline transition-colors';
  const style = active
    ? { background: colour, color: mode === 'light' ? '#FFFFFF' : '#0A0A0A', border: `1px solid ${colour}` }
    : { background: 'transparent', color: 'var(--snt-text-2)', border: `1px solid color-mix(in srgb, ${colour} 45%, var(--snt-line))` };
  const inner = <><span aria-hidden="true" style={{ color: active ? 'inherit' : colour }}>{FLOWER_GLYPH[cat.slug] || '✿'}</span>{cat.name}</>;
  if (onClick) return <button type="button" onClick={onClick} className={cls} style={style} data-testid="category-chip" data-slug={cat.slug} aria-pressed={active}>{inner}</button>;
  return <Link to={to || `/c/${cat.slug}`} className={cls} style={style} data-testid="category-chip" data-slug={cat.slug}>{inner}</Link>;
}

export function SubCategoryChips({ cat, active, onPick, allLabel = 'All' }) {
  const mode = useMode();
  const colour = catColour(cat, mode);
  const subs = cat?.sub_categories || [];
  if (!subs.length) return null;
  const chip = (label, slug, isActive) => (
    <button key={slug || 'all'} type="button" onClick={() => onPick(slug)} aria-pressed={isActive} data-testid="sub-chip" data-slug={slug || ''}
      className="whitespace-nowrap rounded-full px-3.5 py-1.5 text-[12px] font-medium transition-colors"
      style={isActive
        ? { background: colour, color: mode === 'light' ? '#FFFFFF' : '#0A0A0A', border: `1px solid ${colour}` }
        : { background: 'var(--snt-bg-1)', color: 'var(--snt-text-2)', border: '1px solid var(--snt-line)' }}>
      {label}
    </button>
  );
  return (
    <div className="scroll-chips" data-testid="sub-chips">
      {chip(allLabel, '', !active)}
      {subs.map((s) => chip(s.event_count ? `${s.name} · ${s.event_count}` : s.name, s.slug, active === s.slug))}
    </div>
  );
}
